import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TenantProvider } from '@/contexts/tenant-context';
import { TenantSelector } from '@/components/tenants/tenant-selector';
import { CreateTenantDialog } from '@/components/tenants/create-tenant-dialog';
import { TenantMembers } from '@/components/tenants/tenant-members';
import { TenantSettings } from '@/components/tenants/tenant-settings';
import { Building, Users, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { NotificationsSettings } from '@/components/notifications/notification-settings';

export const Route = createFileRoute('/_authenticated/(user-dashboard)/dashboard/_dashboard/tenants')({
  component: TenantsPage,
  validateSearch: (search) => ({
    tab: (search.tab as string) || 'members',
  }),
});

function TenantsPage() {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const navigate = useNavigate({ from: '/dashboard/tenants' });
  const { tab } = Route.useSearch();
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);

  const handleTabChange = (value: string) => {
    navigate({
      search: (prev) => ({ ...prev, tab: value }),
      replace: true,
    });
  };

  return (
    <TenantProvider>
      <div className="container mx-auto py-6">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Tenant Management</h1>
            <p className="text-muted-foreground">Manage your tenants, team members, and settings</p>
          </div>
          <TenantSelector onCreateClick={() => setIsCreateDialogOpen(true)} />
        </div>
          <Tabs value={tab} onValueChange={handleTabChange} className="space-y-6">
            <div className="flex items-center justify-between mb-4">
            <TabsList className="grid w-full max-w-md grid-cols-3">
              <TabsTrigger value="members" className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                <span className="hidden sm:inline">Members</span>
              </TabsTrigger>
              <TabsTrigger value="settings" className="flex items-center gap-2">
                <Settings className="h-4 w-4" />
                <span className="hidden sm:inline">Settings</span>
              </TabsTrigger>
              <TabsTrigger value="billing" className="flex items-center gap-2">
                <Building className="h-4 w-4" />
                <span className="hidden sm:inline">Billing</span>
              </TabsTrigger>
            </TabsList>

            <Button variant="outline" size="sm" onClick={() => setIsNotificationsOpen(true)}>
              <Settings className="w-4 h-4 mr-2" />
              Configure Notifications
            </Button>

            </div>

            <TabsContent value="members">
              <TenantMembers />
            </TabsContent>

            <TabsContent value="settings">
              <TenantSettings />
            </TabsContent>

            <TabsContent value="billing">
              <div className="flex items-center justify-center h-64 border rounded-lg">
                <p className="text-muted-foreground">Billing features coming soon</p>
              </div>
            </TabsContent>
          </Tabs>

        <CreateTenantDialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen} />
        {/* Notification Settings Modal */}
        <NotificationsSettings
          isOpen={isNotificationsOpen}
          onClose={() => setIsNotificationsOpen(false)}
          settingType='general'
        />
      </div>
    </TenantProvider>
  );
}
